import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight, MapPin, Phone, Mail, Clock, User, Navigation, Play } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { todayLocalIso } from "../../lib/dateUtils";

const EXTRAS_LABEL = {
  lavandaria: "Lavandaria",
  consumiveis: "Consumíveis",
  limpeza_profunda: "Limpeza profunda",
  churrasqueira: "Churrasqueira",
  relva: "Relva / jardim",
};

export default function EmployeeDashboard() {
  const [items, setItems] = useState([]);
  const today = todayLocalIso();

  useEffect(() => {
    const load = () => api.get("/bookings").then(({ data }) => setItems(data.items));
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  // Bookings still pending/confirmed/in_progress whose scheduled date has
  // already passed — they MUST stay visible until the cleaning is started,
  // the checklist completed and the booking concluded.
  const overdue = items.filter(b =>
    b.scheduled_date < today
    && ["confirmed", "pending", "in_progress"].includes(b.status),
  );
  const todays = items.filter(b => b.scheduled_date === today && ["confirmed", "in_progress"].includes(b.status));
  const upcoming = items.filter(b => b.scheduled_date > today && ["confirmed", "pending"].includes(b.status));

  return (
    <div className="space-y-6 md:space-y-10">
      <div>
        <div className="eyebrow mb-2 md:mb-3">Olá, bom trabalho</div>
        <h1 className="font-display text-3xl md:text-5xl">Tarefas de hoje.</h1>
        <div className="text-[#a0b2af] text-[10px] md:text-xs mt-2 uppercase tracking-widest">Atualização automática a cada 20s</div>
      </div>

      {/* MOBILE QUICK STATS */}
      <div className="md:hidden grid grid-cols-3 gap-3" data-testid="emp-mobile-stats">
        <div className={`lux-card p-4 text-center ${overdue.length ? "ring-1 ring-[#ef4444]/40" : ""}`}>
          <div className="eyebrow text-[10px]">Atrasadas</div>
          <div className={`font-display text-3xl mt-1 ${overdue.length ? "text-[#fca5a5]" : "text-[#a0b2af]"}`}>{overdue.length}</div>
        </div>
        <div className="lux-card p-4 text-center">
          <div className="eyebrow text-[10px]">Hoje</div>
          <div className="font-display text-3xl text-[#d4af37] mt-1">{todays.length}</div>
        </div>
        <div className="lux-card p-4 text-center">
          <div className="eyebrow text-[10px]">Próximas</div>
          <div className="font-display text-3xl text-[#d4af37] mt-1">{upcoming.length}</div>
        </div>
      </div>

      {overdue.length > 0 && (
        <section data-testid="emp-overdue-section">
          <div className="eyebrow mb-4 text-[#fca5a5] flex items-center gap-2">
            <AlertTriangle size={12} /> Atrasadas — concluir o quanto antes
          </div>
          <div className="space-y-3">
            {overdue.map(b => <TaskRow key={b.id} b={b} overdue today={today} />)}
          </div>
        </section>
      )}

      <section>
        <div className="eyebrow mb-4">Hoje · {today}</div>
        <div className="space-y-3">
          {todays.length === 0 && <div className="text-[#a0b2af] text-sm">Sem tarefas atribuídas para hoje.</div>}
          {todays.map(b => <TaskRow key={b.id} b={b} isToday />)}
        </div>
      </section>

      <section>
        <div className="eyebrow mb-4">Próximas</div>
        <div className="space-y-3">
          {upcoming.length === 0 && <div className="text-[#a0b2af] text-sm">Sem tarefas futuras.</div>}
          {upcoming.map(b => <TaskRow key={b.id} b={b} />)}
        </div>
      </section>
    </div>
  );
}

function TaskRow({ b, isToday = false, overdue = false, today }) {
  const nav = useNavigate();
  const durationLabel = `${Math.floor(b.duration / 60)}h${b.duration % 60 ? ` ${b.duration % 60}m` : ""}`;
  const goTask = () => nav(`/funcionario/tarefa/${b.id}`);
  const todayHighlight = isToday
    ? "ring-2 ring-[#d4af37] bg-[#d4af37]/5 shadow-[0_0_24px_rgba(212,175,55,0.15)]"
    : "";
  const overdueHighlight = overdue
    ? "ring-2 ring-[#ef4444]/60 bg-[#ef4444]/5"
    : "";
  // Days late so the employee sees the urgency
  let daysLate = 0;
  if (overdue && today) {
    const a = new Date(b.scheduled_date);
    const t = new Date(today);
    daysLate = Math.max(0, Math.round((t - a) / 86400000));
  }
  return (
    <div
      role="link" tabIndex={0}
      onClick={goTask}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goTask(); } }}
      className={`block p-5 rounded-sm border-l-4 ev-${b.status === "confirmed" ? "confirmed" : "pending"} hover:-translate-y-0.5 transition-transform cursor-pointer ${todayHighlight} ${overdueHighlight}`}
      data-testid={`emp-task-${b.id}`}
    >
      {overdue && (
        <div className="mb-3 inline-flex items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-widest text-[#fdfbf7] bg-[#ef4444] font-semibold rounded-sm"
             data-testid={`emp-overdue-badge-${b.id}`}>
          <AlertTriangle size={10} /> Atrasada · {daysLate === 0 ? "hoje" : `${daysLate} dia${daysLate > 1 ? "s" : ""}`}
        </div>
      )}
      {isToday && !overdue && (
        <div className="mb-3 inline-flex items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-widest text-[#041f1b] bg-[#d4af37] font-semibold rounded-sm" data-testid={`emp-today-badge-${b.id}`}>
          Hoje
        </div>
      )}
      <div className="grid md:grid-cols-12 gap-4 items-start">
        <div className="md:col-span-2">
          <div className="font-mono text-2xl text-[#d4af37]">{b.scheduled_time}</div>
          <div className="text-[10px] opacity-70 uppercase tracking-widest inline-flex items-center gap-1">
            <Clock size={10} /> {durationLabel}
          </div>
          <div className="font-display text-lg mt-1">{b.apt_type}</div>
        </div>

        <div className="md:col-span-4">
          <div className="text-[10px] uppercase tracking-widest opacity-60">Hóspede</div>
          <div className="text-sm mt-1 inline-flex items-center gap-2">
            <User size={12} className="opacity-60" /> {b.guest_name || b.client_name}
          </div>
          {b.guest_phone && (
            <a href={`tel:${b.guest_phone}`} className="text-xs opacity-80 hover:text-[#d4af37] mt-1 inline-flex items-center gap-2"
               onClick={e => e.stopPropagation()} data-testid={`emp-phone-${b.id}`}>
              <Phone size={11} /> {b.guest_phone}
            </a>
          )}
          {b.client_phone && !b.guest_phone && (
            <a href={`tel:${b.client_phone}`} className="text-xs opacity-80 hover:text-[#d4af37] mt-1 inline-flex items-center gap-2"
               onClick={e => e.stopPropagation()}>
              <Phone size={11} /> {b.client_phone}
            </a>
          )}
          {b.client_email && (
            <div className="text-[11px] opacity-60 mt-1 inline-flex items-center gap-2 truncate">
              <Mail size={10} /> {b.client_email}
            </div>
          )}
        </div>

        <div className="md:col-span-4">
          <div className="text-[10px] uppercase tracking-widest opacity-60">Morada</div>
          {b.apartment_name && <div className="text-sm mt-1">{b.apartment_name}</div>}
          {(b.entry_address || b.apartment_address) && (
            <>
              <div className="text-xs opacity-80 mt-1 inline-flex items-start gap-2">
                <MapPin size={11} className="mt-0.5 flex-shrink-0 text-[#d4af37]" />
                <span>{b.entry_address || b.apartment_address}</span>
              </div>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(b.entry_address || b.apartment_address)}`}
                target="_blank" rel="noreferrer"
                onClick={e => e.stopPropagation()}
                className="text-[11px] text-[#d4af37] hover:underline inline-flex items-center gap-1 mt-1"
                data-testid={`emp-maps-${b.id}`}
              >
                <Navigation size={11} /> Abrir no Google Maps
              </a>
            </>
          )}
        </div>

        <div className="md:col-span-2 text-right">
          {b.status === "confirmed" ? (
            <button
              onClick={async e => {
                e.preventDefault(); e.stopPropagation();
                try {
                  await api.post(`/bookings/${b.id}/start`);
                  toast.success("Serviço iniciado.");
                  window.location.href = `/funcionario/tarefa/${b.id}`;
                } catch (err) { toast.error(formatApiError(err)); }
              }}
              className="btn-gold w-full md:w-auto px-4 py-3 md:py-2 text-sm md:text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
              data-testid={`emp-start-${b.id}`}>
              <Play size={14} /> Iniciar
            </button>
          ) : (
            <ChevronRight className="ml-auto hidden md:block" />
          )}
        </div>
      </div>

      {(b.extras?.length > 0 || b.notes || b.entry_instructions) && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2 text-[11px]">
          {b.extras?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {b.extras.map(e => (
                <span key={e} className="px-2 py-0.5 rounded-sm bg-black/20 border border-white/10">
                  ✦ {EXTRAS_LABEL[e] || e}
                </span>
              ))}
            </div>
          )}
          {b.entry_instructions && (
            <div className="flex items-start gap-2">
              <span className="text-[#d4af37]">🔑</span>
              <div>
                <span className="opacity-60 uppercase tracking-widest text-[10px]">Entrada: </span>
                <span className="opacity-90">{b.entry_instructions}</span>
              </div>
            </div>
          )}
          {b.notes && <div className="opacity-70 italic">📝 {b.notes}</div>}
        </div>
      )}
    </div>
  );
}
